(() => {
  "use strict";

  const focusableSelector = [
    "a[href]",
    "area[href]",
    "button:not([disabled])",
    "input:not([disabled])",
    "select:not([disabled])",
    "textarea:not([disabled])",
    "iframe",
    "summary",
    "[contenteditable='true']",
    "[tabindex]:not([tabindex='-1'])",
  ].join(",");

  function cleanText(value) {
    return String(value || "")
      .replace(/\s+/g, " ")
      .trim();
  }

  function readableFileLabel(src) {
    const file = String(src || "")
      .split("?")[0]
      .split("/")
      .pop()
      ?.replace(/\.(avif|gif|jpe?g|png|svg|webp)$/i, "");

    return cleanText(file?.replace(/[-_]+/g, " "));
  }

  function inferImageAlt(image) {
    const labelledAncestor = image.closest(
      "[aria-label], product-card, .product-card, .collection-card, .featured-blog-posts-card, .hero__container, slideshow-slide",
    );
    const ariaLabel = cleanText(labelledAncestor?.getAttribute("aria-label"));
    if (ariaLabel) return ariaLabel;

    const nearbyText = cleanText(
      image.closest("a, article, li, .resource-list__item, .group-block")?.textContent,
    );
    if (nearbyText) return nearbyText.slice(0, 140);

    const fileLabel = readableFileLabel(image.currentSrc || image.src);
    return fileLabel || "Billionaire Books image";
  }

  function hydrateImageAttributes(root = document) {
    root.querySelectorAll("img").forEach((image) => {
      if (!image.hasAttribute("alt")) {
        image.setAttribute("alt", inferImageAlt(image));
      }

      if (!image.hasAttribute("decoding")) {
        image.setAttribute("decoding", "async");
      }

      const isPriorityHero = image.matches(".slide__image[fetchpriority='high'], .hero__media[fetchpriority='high']");
      if (!isPriorityHero && image.getAttribute("loading") === "eager") {
        const rect = image.getBoundingClientRect();
        if (rect.top > window.innerHeight * 1.25) {
          image.setAttribute("loading", "lazy");
          image.setAttribute("fetchpriority", "low");
        }
      }
    });
  }

  function labelJudgeMeControls(root = document) {
    const arrows = [...root.querySelectorAll("button.jdgm-arrow")];

    arrows.forEach((button, index) => {
      if (!cleanText(button.getAttribute("aria-label"))) {
        button.setAttribute("aria-label", index % 2 === 0 ? "Previous review" : "Next review");
      }

      if (!button.hasAttribute("type")) {
        button.setAttribute("type", "button");
      }
    });
  }

  function setFocusableState(element, hidden) {
    if (hidden) {
      if (!element.hasAttribute("data-nvb-tabindex")) {
        element.setAttribute("data-nvb-tabindex", element.getAttribute("tabindex") ?? "");
      }
      element.setAttribute("tabindex", "-1");
      return;
    }

    if (!element.hasAttribute("data-nvb-tabindex")) return;

    const previous = element.getAttribute("data-nvb-tabindex");
    if (previous) {
      element.setAttribute("tabindex", previous);
    } else {
      element.removeAttribute("tabindex");
    }
    element.removeAttribute("data-nvb-tabindex");
  }

  function guardHiddenSlides(root = document) {
    root.querySelectorAll("slideshow-slide").forEach((slide) => {
      const hidden = slide.getAttribute("aria-hidden") === "true";
      slide.querySelectorAll(focusableSelector).forEach((element) => {
        setFocusableState(element, hidden);
      });
    });
  }

  function runFixes(root = document) {
    hydrateImageAttributes(root);
    labelJudgeMeControls(root);
    guardHiddenSlides(root);
  }

  function scheduleRun(root = document) {
    if ("requestIdleCallback" in window) {
      window.requestIdleCallback(() => runFixes(root), { timeout: 1200 });
      return;
    }

    window.setTimeout(() => runFixes(root), 120);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => scheduleRun(), { once: true });
  } else {
    scheduleRun();
  }

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === "attributes" && mutation.target instanceof Element) {
        if (mutation.target.matches("slideshow-slide")) {
          guardHiddenSlides(mutation.target.parentElement || document);
        }
        continue;
      }

      for (const node of mutation.addedNodes) {
        if (node instanceof Element) {
          scheduleRun(node);
        }
      }
    }
  });

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ["aria-hidden"],
  });
})();
